import { useState, useCallback, useEffect } from 'react'

import axios from '../../../../api/apiClient';
import { token, domain } from '../../../Security';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  GeneralMessage,
  useGeneralMessage
} from '../../../../common/APICommon';
import { getStatusConfig, WorkflowStatus } from '../../../../utils/enums';
import useUser from '../../../../hooks/useUser';

import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoadingSpinner } from '../../../../common/Loading';
import { HRRecruitmentForm } from './HRRecruitmentForm';

export const ProcessorMain = () => {
  const [loading, setLoading] = useState(false);
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const user = useUser();
  const [selectedData, setSelectedData] = useState(null);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({
    candidates: []
  });

  //#region ------------- Search -------------
  const [filteredData, setFilteredData] = useState([]);
  const handleFilter = (value) => {
    setCurrentPage(1);

    const lowercaseValue = value.toLowerCase();

    const res = filteredData.filter((f) => {
      return (
        f?.requester.fullName?.toLowerCase()?.includes(lowercaseValue) ||
        f?.position.description?.toString()?.toLowerCase()?.startsWith(lowercaseValue) ||
        f?.department.description?.toString()?.toLowerCase()?.startsWith(lowercaseValue)
      )
    });

    setData(res);
  };
  //#endregion -------- END Search -----------

  //#region ------------- **Table -------------
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const headers = [
    'Requested Position',
    'Department',
    'Requester',
    'Processor',
    'Requested Date',
    'Cancelled Date',
    'Number Of Vacancies',
    'Is Active?',
    'Status',
    'Actions'
  ]
  const accessors = [
    'position.description',
    'department.description',
    'requester.fullName',
    'processor.fullName',
    'requestedDate',
    'cancelledDate',
    'numberOfVacancies',
    'isActive',
    'status',
    'actions'
  ]

  const viewHandleClick = (data) => {
    setSelectedData(data);
    openHRMRFForm();
  }

  const actions = [
    {
      name: "View",
      tooltip: "view",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-sky-500 hover:bg-sky-600 flex items-center",
      function: viewHandleClick,
      visible: true,
    },
  ];
  const renderCustomCell = (row, accessor) => {
    if (accessor === "status") {
      const config = getStatusConfig(row.status);
      return (
        <span className={`px-3 py-1 text-[11px] rounded-full font-bold border uppercase tracking-wide
        ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
          {row.status}
        </span>
      );
    }
    return null;
  }
  //#endregion -------- END Table -----------

  //#region -------- View Table Data --------
  const [showHRMRFForm, setShowHRMRFForm] = useState(false);
  const openHRMRFForm = () => {
    setShowHRMRFForm(true);
  }
  const closeHRMRFForm = () => {
    setShowHRMRFForm(false);
    resetLocalVariables();
  }
  //#endregion -------- END :: HR ViewMRF Modal --------

  //#region ------------- Accept Status Data ------------
  const handleAccept = async () => {
    const data = {
      status: WorkflowStatus.PROCESSING,
      processorId: user?.id ?? null,
      processingDate: new Date().toISOString(),
    };

    const apiUrl = domain + `/api/MRF/process-request-status/${selectedData.id}`;

    try {
      setLoading(true);
      await axios.put(apiUrl, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await fetchData();
      showGeneralMessage('MRF Request is on process', true);
    } catch (error) {
      let errorMessage = "An unexpected error occurred.";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoading(false);
    }
  }
  //#endregion --------- END Update Status Data ---------

  //#region ------------- Fetch Data ---------
  const fetchData = useCallback(async () => {
    const apiUrl = domain + "/api/MRF";
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showGeneralMessage('Error fetching MRF data', false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchData()
  }, [fetchData])
  //#endregion --------END Fetch Data ---------

  //#region ------------- Save HR Form Data ------------
  const handleSaveHRForm = async (submissionData) => {
    try {
      setLoading(true);

      const apiData = {
        candidates: submissionData.candidates.map(candidate => ({
          id: candidate.id || 0,
          mrfId: submissionData.mrfId,
          // Replaced fullName with the 4 new fields
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          middleName: candidate.middleName,
          extensionName: candidate.extensionName,
          // Optional: You can keep passing fullName if your backend still requires it temporarily
          fullName: `${candidate.lastName || ''}, ${candidate.firstName || ''}`.replace(/\s+/g, ' ').trim(),
          email: candidate.email,
          contactNumber: candidate.contactNumber,
          skills: candidate.skills,
          experience: candidate.experience,
          candidateQualificationNotes: candidate.candidateQualificationNotes,
          processorComments: candidate.processorComments,
          file: candidate.fileBase64 || null,
          fileName: candidate.fileName || null,
          isAccepted: candidate.isAccepted,
          isDeclined: candidate.isDeclined,
          isActive: candidate.isActive
        }))
      };

      const apiUrl = domain + `/api/MRF/candidates/bulk`;

      const response = await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        showGeneralMessage(response.data.message, true);
        await fetchData();
      } else {
        showGeneralMessage(response.data.message, false);
      }

    } catch (error) {
      if (error.response) {
        const errorData = error.response.data;
        showGeneralMessage(errorData.message || 'Failed to save candidates', false);
      } else if (error.request) {
        showGeneralMessage('Network error: Could not connect to server', false);
      } else {
        showGeneralMessage('Error: ' + error.message, false);
      }
    } finally {
      setLoading(false);
    }
  };
  //#endregion --------- END Save HR Form Data ---------

  const resetLocalVariables = () => {
    setSelectedData(null);
    setForm({
      candidates: []
    });
  };

  return (
    <div>
      <SearchBar handleFilter={handleFilter} />

      <CreateTable
        tableName={"MRF"}
        headers={headers}
        accessors={accessors}
        data={data}
        actions={actions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={data?.length}
        customCellRenderer={renderCustomCell}
      />

      <HRRecruitmentForm
        visible={showHRMRFForm}
        onClose={closeHRMRFForm}
        onSave={handleSaveHRForm}
        onAccept={handleAccept}
        modalName="HR Recruitment Form"
        data={selectedData}
        form={form}
        setForm={setForm}
        showGeneralMessage={showGeneralMessage}
      />

      <LoadingSpinner loading={loading} />

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />
    </div>
  );
};